import React from "react";
import Reveal from "./Reveal";
import { useLang } from "../lib/lang-context";
import { ArrowRight } from "@phosphor-icons/react";

/**
 * Stories — alternating image+text storytelling section.
 * Each item: large image (50%) + tag, headline, body, stat block (50%).
 * Image side flips per index for visual rhythm.
 */
export default function Stories() {
  const { t } = useLang();
  return (
    <section
      id="stories"
      data-testid="stories-section"
      className="py-24 md:py-32 bg-white dark:bg-zinc-950 border-t border-slate-200 dark:border-zinc-800"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <Reveal className="max-w-3xl mb-20 md:mb-24">
          <p className="text-xs tracking-[0.24em] uppercase font-bold text-[var(--icor-blue)] eyebrow-line">
            {t.stories.eyebrow}
          </p>
          <h2 className="font-display text-4xl md:text-5xl font-semibold tracking-tighter mt-5 text-[var(--icor-navy)] dark:text-white text-balance">
            {t.stories.title}
          </h2>
        </Reveal>

        <div className="space-y-24 md:space-y-32">
          {t.stories.items.map((s, i) => {
            const flip = i % 2 === 1;
            return (
              <div
                key={i}
                data-testid={`story-${i}`}
                className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center"
              >
                <Reveal
                  variant={flip ? "right" : "left"}
                  className={`lg:col-span-7 ${flip ? "lg:order-2" : ""}`}
                >
                  <div
                    className="relative overflow-hidden border border-slate-200 dark:border-zinc-800 group"
                    style={{ borderRadius: 4 }}
                  >
                    <img
                      src={s.image}
                      alt={s.title}
                      className="w-full aspect-[16/11] object-cover transition-transform duration-1000 group-hover:scale-[1.04]"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent pointer-events-none" />
                    {/* Floating index badge */}
                    <div className="absolute top-5 left-5 text-[10px] tracking-[0.24em] uppercase font-bold text-white bg-black/70 backdrop-blur-sm px-3 py-1.5">
                      {String(i + 1).padStart(2, "0")} / {t.stories.items.length.toString().padStart(2, "0")}
                    </div>
                    {/* Floating stat */}
                    <div
                      className="absolute bottom-5 right-5 bg-white dark:bg-zinc-900 px-5 py-3 shadow-xl"
                      style={{ borderRadius: 4 }}
                    >
                      <div className="font-display text-2xl md:text-3xl font-bold tracking-tighter text-[var(--icor-navy)] dark:text-white leading-none">
                        {s.stat}
                      </div>
                      <div className="text-[10px] tracking-[0.18em] uppercase font-semibold text-slate-500 dark:text-zinc-400 mt-1">
                        {s.statLabel}
                      </div>
                    </div>
                  </div>
                </Reveal>

                <Reveal
                  variant={flip ? "left" : "right"}
                  delay={150}
                  className={`lg:col-span-5 ${flip ? "lg:order-1" : ""}`}
                >
                  <div className="text-xs tracking-[0.24em] uppercase font-bold text-[var(--icor-blue)] mb-4">
                    <span className="inline-block w-7 h-px bg-[var(--icor-blue)] align-middle mr-3" />
                    {s.tag}
                  </div>
                  <h3 className="font-display text-3xl md:text-4xl font-semibold tracking-tighter text-[var(--icor-navy)] dark:text-white text-balance leading-[1.05]">
                    {s.title}
                  </h3>
                  <p className="mt-5 text-base md:text-lg text-slate-600 dark:text-zinc-400 leading-relaxed">
                    {s.body}
                  </p>
                  <a
                    href="#quote"
                    className="mt-7 inline-flex items-center gap-2 text-sm font-medium tracking-tight text-[var(--icor-blue)] hover:underline group"
                  >
                    {t.nav.cta}
                    <ArrowRight
                      size={16}
                      className="transition-transform group-hover:translate-x-1"
                    />
                  </a>
                </Reveal>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
